import { ReactNode, useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

const prefersReducedMotion = () =>
  typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

/** How long the sheet takes to retract: quick, and eased in, so it leaves faster than it arrived. */
export const CALENDAR_EXIT_MS = 220;

const KEYFRAMES = '@keyframes sk-calendar-in{from{transform:translateY(24px);opacity:0}to{transform:translateY(0);opacity:1}}@keyframes sk-calendar-out{from{transform:translateY(0)}to{transform:translateY(100%)}}@keyframes sk-calendar-fade{from{opacity:0}to{opacity:1}}@keyframes sk-calendar-fade-out{from{opacity:1}to{opacity:0}}';

/**
 * CalendarSheet — a bottom sheet over the screen for picking dates. The title is
 * the question ("Lisbon"); the subtitle is the live answer ("Oct 14 – 20 · 7
 * days"), and the footer holds the one button that confirms it. It closes by
 * the backdrop, Escape, the close button, or whatever the footer does, and
 * retracts down rather than vanishing.
 *
 * It sits above the chat (portaled to <body>), and ends above the on-screen
 * keyboard like every other sheet. `status` is read out by screen readers
 * only ("Pick your last day").
 */
export interface CalendarSheetProps {
  open: boolean;
  onClose: () => void;
  title: string;
  /** The line under the title: the answer so far. Its space is reserved from the start, so showing it never moves the sheet. */
  subtitle?: ReactNode;
  /** Announced to screen readers, never shown. */
  status?: string;
  /** The confirm button, under the calendar. */
  footer?: ReactNode;
  children: ReactNode;
}

export function CalendarSheet({ open, onClose, title, subtitle, status, footer, children }: CalendarSheetProps) {
  const titleId = useId();
  const titleRef = useRef<HTMLHeadingElement>(null);
  const [reduceMotion] = useState(prefersReducedMotion);
  // Stays mounted while it retracts, then unmounts.
  const [mounted, setMounted] = useState(open);
  const [leaving, setLeaving] = useState(false);

  useEffect(() => {
    if (open) {
      setMounted(true);
      setLeaving(false);
      return;
    }
    if (!mounted) return;
    if (reduceMotion) {
      setMounted(false);
      return;
    }
    setLeaving(true);
    const t = window.setTimeout(() => { setMounted(false); setLeaving(false); }, CALENDAR_EXIT_MS);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) return;
    titleRef.current?.focus({ preventScroll: true });
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!mounted || typeof document === 'undefined') return null;

  const backdropMotion = reduceMotion ? undefined : leaving ? `sk-calendar-fade-out ${CALENDAR_EXIT_MS}ms ease-in both` : 'sk-calendar-fade 180ms ease-out both';
  const sheetMotion = reduceMotion ? undefined : leaving
    ? `sk-calendar-out ${CALENDAR_EXIT_MS}ms cubic-bezier(.4,0,1,1) both`
    : 'sk-calendar-in 260ms cubic-bezier(.25,.46,.45,.94) both';

  return createPortal(
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 60, display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
        background: 'rgba(0,0,0,.35)', animation: backdropMotion, pointerEvents: leaving ? 'none' : undefined,
      }}
    >
      <style>{KEYFRAMES}</style>
      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%', maxWidth: 440, maxHeight: 'calc((100dvh - var(--kb, 0px)) * 0.92)', overflowY: 'auto',
          background: 'var(--surface)', borderTop: '1px solid var(--line)', borderTopLeftRadius: 20, borderTopRightRadius: 20,
          padding: '10px 16px calc(20px + var(--sab))', display: 'flex', flexDirection: 'column', gap: 14,
          animation: sheetMotion,
        }}
      >
        <div aria-hidden="true" style={{ alignSelf: 'center', width: 36, height: 4, borderRadius: 2, background: 'var(--line)' }} />
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
          <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
            <h2
              id={titleId}
              ref={titleRef}
              tabIndex={-1}
              style={{ margin: 0, fontFamily: 'var(--font-serif)', fontSize: 'var(--fs-body)', fontWeight: 400, letterSpacing: '-0.01em', lineHeight: 1.25, color: 'var(--text)', outline: 'none' }}
            >
              {title}
            </h2>
            {/* The line is always there (empty until there's an answer), so the sheet never changes height as dates are picked. */}
            <p style={{ margin: 0, minHeight: 18, fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-footnote)', lineHeight: '18px', color: 'var(--text)', fontVariantNumeric: 'tabular-nums' }}>{subtitle ?? '\u00a0'}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close calendar"
            className="sk-flow-focus sk-hit"
            style={{ flex: 'none', width: 32, height: 32, borderRadius: '50%', border: 'none', background: 'var(--chip-bg)', color: 'var(--text)', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
          >
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true"><path d="M2 2l8 8M10 2l-8 8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" /></svg>
          </button>
        </div>
        {children}
        {footer}
        <span aria-live="polite" style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap' }}>{status}</span>
      </section>
    </div>,
    document.body,
  );
}

export default CalendarSheet;
